import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/auth.types'
import { authService } from '@/services/auth.service'
import { usePermissionsStore } from '@/stores/permissions'

export const useUserStore = defineStore('user', () => {
  // State
  const profile = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const fullName = computed(() => profile.value?.display_name || '')
  const username = computed(() => profile.value?.username || '')
  const email = computed(() => profile.value?.email || '')
  const isSuperAdmin = computed(() => profile.value?.is_super_admin || false)
  const sectionName = computed(() => profile.value?.section?.name || '')
  const profilePicture = computed(() => profile.value?.avatar || '')

  // Permission helpers - delegating to permissions store
  function hasPermission(permission: string): boolean {
    const permissionsStore = usePermissionsStore()
    return permissionsStore.hasPermission(permission)
  }

  // Actions
  async function fetchProfile() {
    loading.value = true
    error.value = null

    try {
      const userData = await authService.getCurrentUser()
      profile.value = userData
      // Also fetch permissions when profile is fetched
      const permissionsStore = usePermissionsStore()
      await permissionsStore.fetchPermissions({ force: true })
      return true
    } catch (err: any) {
      error.value = err.message
      return false
    } finally {
      loading.value = false
    }
  }

  function setProfile(user: User) {
    profile.value = user
  }

  function clearProfile() {
    profile.value = null
    const permissionsStore = usePermissionsStore()
    permissionsStore.clearPermissions()
  }

  return {
    // State
    profile,
    loading,
    error,
    // Getters
    fullName,
    username,
    email,
    isSuperAdmin,
    sectionName,
    profilePicture,
    // Permission methods
    hasPermission,
    // Actions
    fetchProfile,
    setProfile,
    clearProfile
  }
})
